import { Box, IconButton } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableColumnDraft } from 'entities/Files/types';
import { ColorPalette } from 'shared/consts/colorPalette';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { Icon } from 'shared/ui/Icon';
import { Input } from 'shared/ui/Input';
import { Paper } from 'shared/ui/Paper';

const isSingleWord = (value: string) => !/\s/.test(value.trim());

const EMPTY_COLUMN: TableColumnDraft = { columnName: '', sourceIndex: null };

type AddTableFormProps = {
	initialTableName?: string;
	initialColumns?: TableColumnDraft[];
	onCancel: () => void;
	onSave: (tableName: string, columns: TableColumnDraft[]) => void;
};

const AddTableForm: FC<AddTableFormProps> = ({ initialTableName, initialColumns, onCancel, onSave }) => {
	const { t } = useTranslation();
	const [tableName, setTableName] = useState(initialTableName ?? '');
	const [columns, setColumns] = useState<TableColumnDraft[]>(
		initialColumns?.length ? initialColumns : [EMPTY_COLUMN],
	);

	const trimmedName = tableName.trim();
	const trimmedColumns = columns
		.map((column) => ({ ...column, columnName: column.columnName.trim() }))
		.filter((column) => column.columnName);
	const oneWordError = t('file_editor.one_word_error');
	const isNameValid = isSingleWord(tableName);
	const areColumnsValid = columns.every((column) => isSingleWord(column.columnName));
	const isSaveDisabled = !trimmedName || !trimmedColumns.length || !isNameValid || !areColumnsValid;

	const handleColumnChange = (columnIndex: number, value: string) => {
		setColumns((prev) =>
			prev.map((column, index) => (index === columnIndex ? { ...column, columnName: value } : column)),
		);
	};

	const handleAddColumn = () => {
		setColumns((prev) => [...prev, EMPTY_COLUMN]);
	};

	const handleRemoveColumn = (columnIndex: number) => {
		setColumns((prev) => prev.filter((_, index) => index !== columnIndex));
	};

	const handleSave = () => {
		if (isSaveDisabled) return;
		onSave(trimmedName, trimmedColumns);
	};

	return (
		<Paper
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: 2,
				padding: 2,
				width: '100%',
			}}
		>
			<Input
				sx={{ margin: 0 }}
				name={'tableName'}
				label={t('files.labels.table_name')}
				placeholder={t('files.placeholder.table_name') as string}
				type={'text'}
				required
				onChange={(event) => setTableName(event.target.value)}
				value={tableName}
				errorText={isNameValid ? undefined : oneWordError}
			/>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
					{columns.map((column, columnIndex) => (
						<Box key={columnIndex} sx={{ position: 'relative', flex: '1 1 160px' }}>
							<Input
								sx={{ margin: 0 }}
								name={`columnName-${columnIndex}`}
								label={''}
								placeholder={t('file_editor.column_name_placeholder') as string}
								type={'text'}
								onChange={(event) => handleColumnChange(columnIndex, event.target.value)}
								value={column.columnName}
								errorText={isSingleWord(column.columnName) ? undefined : oneWordError}
							/>
							{columns.length > 1 && (
								<IconButton
									size={'small'}
									onClick={() => handleRemoveColumn(columnIndex)}
									sx={{
										'position': 'absolute',
										'top': '-8px',
										'right': '-8px',
										'padding': 0,
										'backgroundColor': ColorPalette.white,
										'&:hover': { backgroundColor: ColorPalette.white },
									}}
								>
									<Icon
										id={'close_round'}
										color={ColorPalette.violetRed}
										style={{ width: '16px', height: '16px' }}
									/>
								</IconButton>
							)}
						</Box>
					))}
				</Box>
				<IconButton size={'small'} sx={{ padding: '4px' }} onClick={handleAddColumn}>
					<Icon
						id={'add_round'}
						color={ColorPalette.royalBlue}
						style={{ width: '20px', height: '20px' }}
					/>
				</IconButton>
			</Box>
			<ActionsForDialog
				applyActionDisabled={isSaveDisabled}
				cancelLabel={t('shared_texts.cancel')}
				cancelAction={onCancel}
				applyLabel={t('shared_texts.save')}
				applyAction={handleSave}
			/>
		</Paper>
	);
};

export default AddTableForm;
